import { feature } from "topojson-client";
import type { Topology } from "topojson-specification";
import statesTopology from "us-atlas/states-10m.json";
import { location } from "../../../data/Fleet/Location";

type Coordinates = { latitude: number; longitude: number };
type Ring = number[][];
type Polygon = Ring[];

interface StateShape {
  name: string;
  code: string;
  polygons: Array<{
    rings: Polygon;
    bounds: [number, number, number, number];
  }>;
}

const stateCodes = new Map(location.map(({ value, label }) => [label, value]));
const topology = statesTopology as unknown as Topology;
const stateFeatures = feature(topology, topology.objects.states);

// Decode the simplified Census boundaries once. A bounding-box check avoids
// walking polygon vertices for almost every shop/state pair.
const stateShapes: StateShape[] = stateFeatures.type === "FeatureCollection"
  ? stateFeatures.features.flatMap(({ properties, geometry }) => {
      const name = properties?.name;
      if (typeof name !== "string") return [];

      const polygons = geometry.type === "Polygon"
        ? [geometry.coordinates]
        : geometry.type === "MultiPolygon"
          ? geometry.coordinates
          : [];

      return [{
        name,
        code: stateCodes.get(name) || name,
        polygons: polygons.map((rings) => {
          const longitudes = rings[0].map(([longitude]) => longitude);
          const latitudes = rings[0].map(([, latitude]) => latitude);
          return {
            rings,
            bounds: [
              Math.min(...longitudes), Math.min(...latitudes),
              Math.max(...longitudes), Math.max(...latitudes),
            ] as [number, number, number, number],
          };
        }),
      }];
    })
  : [];

function isInsideRing(longitude: number, latitude: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[j];
    if ((y1 > latitude) !== (y2 > latitude) &&
        longitude < ((x2 - x1) * (latitude - y1)) / (y2 - y1) + x1) {
      inside = !inside;
    }
  }
  return inside;
}

export function getShopState({ latitude, longitude }: Coordinates) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  for (const state of stateShapes) {
    for (const { rings, bounds } of state.polygons) {
      if (longitude < bounds[0] || latitude < bounds[1] ||
          longitude > bounds[2] || latitude > bounds[3]) continue;
      if (isInsideRing(longitude, latitude, rings[0]) &&
          !rings.slice(1).some((hole) => isInsideRing(longitude, latitude, hole))) {
        return { name: state.name, code: state.code };
      }
    }
  }
  return null;
}

export function groupShopsByState<T extends Coordinates>(shops: T[]) {
  const byState = new Map<string, { name: string; code: string; shops: T[] }>();
  const individual: T[] = [];

  for (const shop of shops) {
    const state = getShopState(shop);
    if (!state) {
      individual.push(shop);
      continue;
    }
    let group = byState.get(state.name);
    if (!group) {
      group = { ...state, shops: [] };
      byState.set(state.name, group);
    }
    group.shops.push(shop);
  }

  const grouped = [] as Array<{
    name: string;
    code: string;
    shops: T[];
    position: [number, number];
  }>;

  for (const group of Array.from(byState.values())) {
    if (group.shops.length === 1) {
      individual.push(group.shops[0]);
      continue;
    }

    const average = group.shops.reduce(
      (sum, shop) => ({
        latitude: sum.latitude + shop.latitude,
        longitude: sum.longitude + shop.longitude,
      }),
      { latitude: 0, longitude: 0 },
    );
    average.latitude /= group.shops.length;
    average.longitude /= group.shops.length;
    // Place the badge on an actual shop so it stays inside its state.
    const representative = group.shops.reduce((closest, shop) =>
      (shop.latitude - average.latitude) ** 2 + (shop.longitude - average.longitude) ** 2 <
      (closest.latitude - average.latitude) ** 2 + (closest.longitude - average.longitude) ** 2
        ? shop : closest,
    );
    grouped.push({
      ...group,
      position: [representative.latitude, representative.longitude],
    });
  }

  return { grouped, individual };
}
